import React, {
  useContext, useEffect, useRef
} from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';
import { LegalComplianceMessage } from './drawer/LegalComplianceMessage';
import { AssistantLoader } from './drawer/AssistantLoader';
import { ChatMessage } from './ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';
import { useAssistant } from '../AssistantProvider';
import { useDrawer } from '../DrawerProvider';
import { TryAgainButton } from './TryAgainButton';
import { MESSAGE_ORIGIN_PREPOPULATED, MESSAGE_ORIGIN_RETRY } from '../constants';

const MESSAGE_INTERRUPTED = 'The conversation was interrupted before I could finish finding the answer.';
const RETRY_MESSAGE = {
  type: 'bot',
  content: {
    responseSummary: {
      summaryAnswer: MESSAGE_INTERRUPTED
    },
  }
};

export const DrawerContent = () => {
  const { channel } = useContext(ExperienceContext);
  const {
    messages,
    historyCleared,
    historyLoading,
    askUserQuestion,
    botLoading,
  } = useAssistant();
  const { scrollToBottom } = useDrawer();

  const haveMessagesMounted = useRef(false);
  const lastUserMessageRef = useRef(null);

  const isMobile = channel === 'mobile';
  const lastBotMessage = messages.findLast(({ type }) => type === 'bot');
  const lastUserMessage = messages.findLast(({ type }) => type === 'user');
  const lastAtcConfirmationIndex = messages.findLastIndex(({ type }) => type === 'add-to-cart');
  const isLastMessageATCConfirmation = lastAtcConfirmationIndex === messages.length - 1;
  const isLastMessageFromUser = lastUserMessage && lastUserMessage === messages[messages.length - 1];

  const showRetryPrompt = isLastMessageFromUser && !botLoading;
  const showSuggestedQuestions = !botLoading && !showRetryPrompt && !isLastMessageFromUser;

  useEffect(() => {
    // Scrolls to bottom on resize. Primarily needed for mobile minimized to open state.
    const debouncedSTB = debounce(100, () => scrollToBottom());

    window.addEventListener('resize', debouncedSTB);

    return () => window.removeEventListener('resize', debouncedSTB);
  }, []);

  useEffect(() => {
    if (!historyLoading) {
      // This was scrolling the main page as well on load, timeout seems to only scroll the drawer.
      setTimeout(() => {
        scrollToBottom();
      }, 1);
    }
  }, [historyLoading]);

  useEffect(() => {
    if (!isLastMessageATCConfirmation && lastUserMessageRef.current && haveMessagesMounted.current) {
      // Scroll the drawer to the last user message, centering it in the drawer. scrollIntoView method didn't work
      // as expected, it was causing the parent page to scroll, so we calculate the position manually.
      const messageElement = lastUserMessageRef.current;
      const drawerElement = messageElement.closest('[data-testid="drawer-body"]');

      if (drawerElement && messageElement) {
        const messagePosition = messageElement.offsetTop;
        const drawerHeight = drawerElement.clientHeight;
        const messageHeight = messageElement.offsetHeight;

        const centeredPosition = messagePosition - (drawerHeight / 2) + (messageHeight / 2);
        const maxScrollTop = drawerElement.scrollHeight - drawerElement.clientHeight; // Prevents overscroll!

        drawerElement.scrollTo({
          top: Math.max(0, Math.min(centeredPosition, maxScrollTop)),
          behavior: 'smooth'
        });
      }
    }

    if (isLastMessageATCConfirmation && haveMessagesMounted.current) {
      scrollToBottom();
    }

    // We want to ignore the first render of messages since we should be scrolled all the way to the bottom.
    haveMessagesMounted.current = true;
  }, [botLoading, messages]);

  return (
    <>
      <div data-testid="drawer-message-history">
        <LegalComplianceMessage isMobile={isMobile} />
        {historyCleared && messages.length === 1 && (
          <Typography align="center" color="subtle" variant="body-base">Conversation cleared</Typography>
        )}
        <div className="sui-flex sui-flex-col">
          {historyLoading ? (
            <div className="sui-flex-col sui-align-center sui-py-4 sui-w-full">
              <Typography variant="body-base" color="subtle" align="center">
                Loading messages
              </Typography>
              <AssistantLoader align="center" />
            </div>
          ) : messages?.map((message, i) => {
            const showFeedback = (
              message.type === 'bot'
              && !message.feedbackProvided
              && message === lastBotMessage
              && i !== 0
            );
            return (
              <ChatMessage
                key={i}
                message={message}
                showFeedback={showFeedback}
                ref={message === lastUserMessage ? lastUserMessageRef : null}
              />
            );
          })}
          {showRetryPrompt && (
            <>
              <ChatMessage
                message={RETRY_MESSAGE}
                showFeedback={false}
              />
              <TryAgainButton
                value={lastUserMessage.content}
                onClick={async (question) => {
                  askUserQuestion({ userQuestion: question, messageOrigin: MESSAGE_ORIGIN_RETRY });
                }}
              />
            </>
          )}
        </div>
        {botLoading && <AssistantLoader />}
      </div>
      {showSuggestedQuestions && (
        <SuggestedQuestions
          onClick={async (question, suggestedQuestionPosition, isInitialSuggestedQuestion) => {
            askUserQuestion({
              userQuestion: question,
              messageOrigin: MESSAGE_ORIGIN_PREPOPULATED,
              suggestedQuestionPosition,
              isInitialSuggestedQuestion,
            });
          }}
        />
      )}
    </>
  );
};

DrawerContent.propTypes = {};
